/**
 * CaseUpdateComposer - Teams-style update composer for case items.
 * Supports: optional title, message body, drag-and-drop attachments, image
 * upload, internal-note marking, links, tags (Report, Contact, Customer,
 * Project), priority and Ctrl/Cmd+Enter to send.
 *
 * Mobile (≤860px, same as CaseView): collapsed by default — user taps to open
 * the editor. Desktop keeps the always-available composer.
 */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PaperAirplaneIcon,
  PaperClipIcon,
  PencilSquareIcon,
  PhotoIcon,
  PlusIcon,
  TagIcon,
  XMarkIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";
import { DocumentUploadSectionV4 } from "../Media/DocumentUploadSectionV4";
import { MentionTextEditor, extractMentionedUsers, extractModuleTags } from "../../utils/MentionTextEditor";
import { TWclassNames } from "../../utils/Div";
import { priorityOptions } from "../quality/deviations/utils";
// Gjenbruk nøyaktig samme diktering/transkribering som Statusoppdatering-composeren
// (delt komponent + delt API `transcribeAudio`), slik at fremtidige API-endringer
// slår igjennom overalt uten duplisert logikk.
import { VoiceTranscriptButton } from "../statusUpdates/VoiceTranscriptButton";

const PRIO_DOT = {
  "1": "#94A3B8",
  "2": "#F59E0B",
  "3": "#F97316",
  "4": "#E11D48",
};

/** Match CaseView mobile breakpoint (case-view-redesign.css). */
const MOBILE_MQ = "(max-width: 860px)";

function useIsMobileCaseView() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return isMobile;
}

export function CaseUpdateComposer({
  caseId,
  onSubmit,
  disabled = false,
  compact = false,
  customer,
  /** When true, only users with case role (can read/edit cases) can be tagged */
  restrictMentionsToCaseUsers = true,
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobileCaseView();
  /** Mobile: closed until the user taps «Skriv en kommentar». Desktop always open. */
  const [composerOpen, setComposerOpen] = useState(() => !isMobile);
  const [showTitle, setShowTitle] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState(""); // HTML from MentionTextEditor
  const [documents, setDocuments] = useState([]);
  const [tags, setTags] = useState([]); // From slash / picks via onTagSelect
  const [priority, setPriority] = useState(null); // 1=Low, 2=Medium, 3=High, 4=Critical
  const [prioOpen, setPrioOpen] = useState(false);
  const [internal, setInternal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const dragDepth = useRef(0);
  const stageFilesRef = useRef(null); // set by DocumentUploadSectionV4 via addFilesRef
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Keep desktop always open; reset to closed when entering the mobile breakpoint.
  useEffect(() => {
    setComposerOpen(!isMobile);
  }, [isMobile]);

  const hasContent =
    (message?.trim() && message !== "<p><br></p>") ||
    documents?.length > 0 ||
    tags?.length > 0 ||
    title?.trim();

  const handleUploadCallback = (uploadedDocs) => {
    const merged = [...documents, ...uploadedDocs].filter(
      (v, i, a) => a.findIndex((d) => d.id === v.id) === i
    );
    setDocuments(merged);
  };

  const stageFiles = (fileList) => {
    if (!fileList || !fileList.length) return;
    if (typeof stageFilesRef.current === "function") {
      stageFilesRef.current(fileList);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    if (disabled) return;
    const files = e.dataTransfer?.files;
    stageFiles(files);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    if (disabled) return;
    dragDepth.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const removeDocument = (id) => {
    setDocuments((prev) => prev.filter((d) => d.id !== id));
  };

  // Setter inn transkribert tekst i meldingsfeltet. Speiler Statusoppdatering sin
  // `appendTranscript`: bruk body-HTML fra API-et hvis det finnes, ellers escape
  // råteksten og legg den til som et avsnitt. Foreslått tittel fylles inn dersom
  // brukeren ikke allerede har skrevet en.
  const appendTranscript = (text, meta = {}) => {
    const bodyHtml = meta?.bodyHtml;
    const suggestedTitle = meta?.title;
    if (!text && !bodyHtml) return;
    const clean = message && message !== "<p><br></p>" ? message : "";
    let next;
    if (bodyHtml && bodyHtml.trim()) {
      next = `${clean}${bodyHtml}`;
    } else {
      const safe = String(text).replace(/</g, "&lt;").replace(/>/g, "&gt;");
      next = `${clean}<p>${safe}</p>`;
    }
    setMessage(next);
    if (suggestedTitle && suggestedTitle.trim() && !title?.trim()) {
      setShowTitle(true);
      setTitle(suggestedTitle.trim());
    }
  };

  const handleSubmit = async () => {
    if (!hasContent || !onSubmit || !caseId || disabled || isSubmitting) return;

    let description = message?.trim() || "";
    if (description === "<p><br></p>") description = "";

    const mentionedUsers = extractMentionedUsers(message || "");
    const userTags = mentionedUsers.map((u) => ({
      module: "user",
      module_param: u.id,
    }));
    const slashTags = extractModuleTags(message || "");
    const tagKeys = new Set(tags.map((tg) => `${tg.module}:${tg.module_param}`));
    const mergedSlashTags = slashTags.filter(
      (tg) => !tagKeys.has(`${tg.module}:${tg.module_param}`)
    );
    const allTags = [...tags, ...mergedSlashTags, ...userTags];
    const notifyEmployees =
      mentionedUsers.length > 0 ? mentionedUsers.map((u) => ({ id: u.id })) : undefined;

    // API expects tags as [{ module, module_param }] - ensure valid format, filter invalid
    const tagsForApi =
      allTags.length > 0
        ? allTags
            .map((tg) => ({
              module: String(tg.module || ""),
              module_param: tg.module_param != null ? String(tg.module_param) : "",
            }))
            .filter((tg) => tg.module && tg.module_param)
        : undefined;

    // Internt notat er (per nå) en ren merkelapp: når aktiv settes tittelen til
    // «Internt notat» dersom ingen egen tittel er skrevet. Ingen falsk «kun
    // synlig internt»-garanti — full privat synlighet krever egen API-støtte.
    const effectiveTitle = internal
      ? title?.trim() || t("Internt notat")
      : title?.trim();

    const payload = {
      case_id: caseId,
      title: effectiveTitle,
      description: description || " ",
      action: "ADDED",
      module: "description",
      documents: documents.map((d) => ({ id: d.id })),
      tags: tagsForApi,
      notifyEmployees: notifyEmployees,
      priority: priority != null ? String(priority) : undefined,
    };

    setIsSubmitting(true);
    try {
      await onSubmit(payload);
      setMessage("");
      setTitle("");
      setDocuments([]);
      setTags([]);
      setPriority(null);
      setShowTitle(false);
      setPrioOpen(false);
      setInternal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedPrio = priorityOptions.find((o) => String(o.value) === String(priority));

  // Mobile: compact trigger until the user chooses to compose.
  if (isMobile && !composerOpen) {
    return (
      <button
        type="button"
        className="case-update-composer-trigger flex w-full min-h-[44px] items-center gap-2.5 rounded-[14px] border border-[#DDE4EE] bg-white px-3.5 py-2.5 text-left text-sm font-semibold text-slate-500 shadow-[0_4px_16px_rgba(15,35,66,0.07)]"
        onClick={() => !disabled && setComposerOpen(true)}
        disabled={disabled}
        aria-expanded={false}
      >
        <PlusIcon className="h-5 w-5 shrink-0 text-[#1463C7]" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">
          {t("case.view.composer_open")}
        </span>
        <PencilSquareIcon className="h-5 w-5 shrink-0 text-[#1463C7]" aria-hidden="true" />
      </button>
    );
  }

  return (
    <div
      className={TWclassNames(
        "case-update-composer relative flex min-h-0 flex-col rounded-xl border bg-white shadow-[0_4px_16px_rgba(15,35,66,0.07)]",
        internal ? "border-amber-300 ring-1 ring-amber-200" : "border-[#DDE4EE]",
        compact && "text-sm"
      )}
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
    >
      {/* Hidden uploader — reuses the shared preview/upload flow. Its own button
          is hidden; we drive it from our own toolbar + drag-drop via addFilesRef. */}
      <div className="hidden">
        <DocumentUploadSectionV4
          iconOnly
          compact
          module="CASE"
          module_param={caseId}
          moduleParam={caseId}
          multiple
          parentCallback={handleUploadCallback}
          addFilesRef={stageFilesRef}
        />
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="sr-only"
        onChange={(e) => {
          stageFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={imageInputRef}
        type="file"
        multiple
        accept="image/*"
        className="sr-only"
        onChange={(e) => {
          stageFiles(e.target.files);
          e.target.value = "";
        }}
      />

      {isDragging && (
        <div className="composer-dropzone absolute inset-0 z-30 flex items-center justify-center rounded-xl border-2 border-dashed border-[#1B6FE0] bg-[#EBF2FE]/90 text-sm font-semibold text-[#1B6FE0]">
          {t("Slipp filer her for å laste opp")}
        </div>
      )}

      {isMobile && (
        <div className="composer-mobile-chrome flex shrink-0 items-center justify-between gap-2 border-b border-[#E5E9F0] px-3 py-2">
          <span className="text-xs font-semibold text-slate-500">
            {t("case.view.composer_open")}
          </span>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            onClick={() => setComposerOpen(false)}
            aria-label={t("Close")}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      <div className="composer-body min-h-0 flex-1 overflow-y-auto">
      {internal && (
        <div className="flex items-center gap-1.5 border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-xs font-semibold text-amber-700">
          <TagIcon className="h-3.5 w-3.5" />
          {t("Internt notat")}
        </div>
      )}

      {showTitle && (
        <div className="border-b border-gray-100 px-4 py-2">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("Title (optional)")}
            className="w-full rounded-lg border-0 bg-transparent py-1 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
          />
        </div>
      )}

      <div className="composer-editor min-h-0 overflow-y-auto px-4 pt-3 pb-1" onKeyDown={handleKeyDown}>
        <MentionTextEditor
          value={message}
          onChange={setMessage}
          placeholder={t("case.view.composer_placeholder")}
          minHeight={isMobile ? 88 : compact ? 92 : 140}
          maxHeight={isMobile ? 176 : compact ? 180 : 240}
          disabled={disabled}
          userFilters={restrictMentionsToCaseUsers ? [{ name: "role", value: "case" }] : []}
          enableSlashCommands={true}
          onTagSelect={(tag) => setTags((prev) => [...prev, tag])}
          customer={customer}
        />
      </div>

      {documents?.length > 0 && (
        <div className="composer-attachments flex flex-wrap gap-2 border-t border-gray-100 px-4 py-2.5">
          {documents.map((doc) => {
            const isImage =
              typeof doc?.mime_type === "string" && doc.mime_type.startsWith("image/");
            const name = doc?.title || doc?.name || doc?.file_name || t("Dokument");
            const sizeKb = typeof doc?.size_kb === "number" ? doc.size_kb : null;
            const meta =
              sizeKb != null
                ? sizeKb >= 1024
                  ? `${(sizeKb / 1024).toFixed(1)} MB`
                  : `${Math.round(sizeKb)} KB`
                : isImage
                  ? t("Bilde")
                  : t("Dokument");
            return (
              <span key={doc.id} className="composer-attachment">
                {isImage && doc?.url ? (
                  <span
                    className="composer-attachment-thumb"
                    style={{ backgroundImage: `url(${doc.url})` }}
                    title={name}
                  />
                ) : (
                  <span className="composer-attachment-thumb is-file">
                    <DocumentIcon className="h-4 w-4 text-[#1B6FE0]" />
                  </span>
                )}
                <span className="composer-attachment-meta">
                  <span className="composer-attachment-name">{name}</span>
                  <span className="composer-attachment-sub">{meta}</span>
                </span>
                <button
                  type="button"
                  onClick={() => removeDocument(doc.id)}
                  className="composer-attachment-remove"
                  aria-label={t("Remove")}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      </div>

      <div className="composer-toolbar flex shrink-0 items-center justify-between gap-2 flex-wrap px-2 py-2">
        <div className="composer-toolbar-tools flex min-w-0 flex-1 items-center gap-0.5 flex-wrap">
          <button
            type="button"
            onClick={() => setShowTitle(!showTitle)}
            aria-pressed={showTitle}
            className={TWclassNames(
              "rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700",
              showTitle && "bg-slate-100 text-[#1B6FE0]"
            )}
            title={t("Add title")}
            aria-label={t("Add title")}
          >
            <PlusIcon className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={() => !disabled && fileInputRef.current?.click()}
            className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
            title={t("Attach file")}
            aria-label={t("Attach file")}
          >
            <PaperClipIcon className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={() => !disabled && imageInputRef.current?.click()}
            className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
            title={t("Add image")}
            aria-label={t("Add image")}
          >
            <PhotoIcon className="h-4 w-4" />
          </button>

          {/* Diktering — delt komponent/API med Statusoppdatering-composeren */}
          <VoiceTranscriptButton onTranscript={appendTranscript} disabled={disabled} />

          <div className="w-px h-[18px] bg-[#E5E9F0] mx-1.5" />

          <button
            type="button"
            onClick={() => setInternal((v) => !v)}
            aria-pressed={internal}
            className={TWclassNames(
              "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors",
              internal
                ? "bg-amber-100 text-amber-700"
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            )}
            title={t("Internt notat")}
          >
            <TagIcon className="h-4 w-4" />
            {t("Internt notat")}
          </button>

          <div className="relative inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPrioOpen((o) => !o)}
              aria-expanded={prioOpen}
              aria-haspopup="listbox"
              className={TWclassNames(
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold",
                priority
                  ? "bg-[#EBF2FE] text-[#1B6FE0]"
                  : "bg-[#F8FAFC] text-slate-500"
              )}
              title={t("Prioritering")}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: PRIO_DOT[String(priority)] || "#94A3B8" }}
              />
              {selectedPrio ? t(String(selectedPrio.label)) : t("Priority")}
            </button>
            {priority != null && (
              <button
                type="button"
                onClick={() => {
                  setPriority(null);
                  setPrioOpen(false);
                }}
                className="rounded-md px-1.5 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-rose-600"
                aria-label={t("Clear")}
              >
                ×
              </button>
            )}
            {prioOpen && (
              <div
                role="listbox"
                className="absolute bottom-full left-0 mb-1.5 w-[140px] rounded-[10px] border border-[#E5E9F0] bg-white p-1 shadow-[0_8px_24px_rgba(15,35,66,0.12)] z-20 flex flex-col"
              >
                {priorityOptions.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={String(priority) === String(o.value)}
                    onClick={() => {
                      setPriority(o.value);
                      setPrioOpen(false);
                    }}
                    className={TWclassNames(
                      "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] font-semibold text-slate-700 hover:bg-slate-100 text-left",
                      String(priority) === String(o.value) && "bg-[#EBF2FE]"
                    )}
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: PRIO_DOT[String(o.value)] || "#94A3B8" }}
                    />
                    {t(String(o.label))}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!hasContent || disabled || isSubmitting}
          className={TWclassNames(
            "composer-send flex min-h-[44px] shrink-0 items-center justify-center gap-1.5 rounded-[9px] px-[18px] py-2 text-[13px] font-semibold transition-colors",
            hasContent && !disabled && !isSubmitting
              ? "bg-[#1B6FE0] text-white hover:bg-[#0F55B8]"
              : "cursor-not-allowed bg-slate-100 text-slate-400"
          )}
          title={t("Ctrl+Enter for å sende")}
        >
          {t("Send")}
          <PaperAirplaneIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

export default CaseUpdateComposer;
